import { Alert } from "react-native"

export interface ImageOrVideo {
  path: string
  localIdentifier?: string
  sourceURL?: string
  filename?: string
  width: number
  height: number
  mime: string
  size: number
  data?: string
  exif?: any
  cropRect?: {
    x: number
    y: number
    width: number
    height: number
  }
  creationDate?: string
  modificationDate?: string
}

export interface Options {
  cropping?: boolean
  width?: number
  height?: number
  includeExif?: boolean
  includeBase64?: boolean
  avoidEmptySpaceAroundImage?: boolean
  cropperActiveWidgetColor?: string
  cropperStatusBarColor?: string
  cropperToolbarColor?: string
  cropperToolbarWidgetColor?: string
  freeStyleCropEnabled?: boolean
  hideBottomControls?: boolean
  enableRotationGesture?: boolean
  disableCropperColorSetters?: boolean
  minFiles?: number
  maxFiles?: number
  waitAnimationEnd?: boolean
  smartAlbums?: any[]
  useFrontCamera?: boolean
  compressVideoPreset?: string
  compressImageMaxWidth?: number
  compressImageMaxHeight?: number
  compressImageQuality?: number
  loadingLabelText?: string
  mediaType?: "photo" | "video" | "any"
  showsSelectedCount?: boolean
  forceJpg?: boolean
  showCropGuidelines?: boolean
  showCropFrame?: boolean
  multiple?: boolean
  writeTempFile?: boolean

  cropperCircleOverlay?: boolean
  sortOrder?: "none" | "asc" | "desc"
  cropperChooseText?: string
  cropperCancelText?: string
  cropperRotateButtonsHidden?: boolean
}

const createMockImage = (): ImageOrVideo => ({
  path: "/placeholder.jpg",
  width: 512,
  height: 512,
  mime: "image/jpeg",
  size: 50000,
  filename: "mock-image.jpg",
  data: "",
})

const ImageCropPicker = {
  openPicker: (options?: Options): Promise<ImageOrVideo | ImageOrVideo[]> => {
    return new Promise((resolve, reject) => {
      // Simula a seleção de imagem na web
      const input = document.createElement("input")
      input.type = "file"
      input.accept = "image/*"
      input.multiple = options?.multiple || false

      input.onchange = (event: any) => {
        const files = event.target.files
        if (!files || files.length === 0) {
          reject(new Error("User cancelled image selection"))
          return
        }

        const processFile = (file: any): any => {
          return new Promise((resolveFile) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              const img = new Image()
              img.onload = () => {
                const result: ImageOrVideo = {
                  path: e.target?.result as string,
                  width: img.width,
                  height: img.height,
                  mime: file.type,
                  size: file.size,
                  filename: file.name,
                  data: options?.includeBase64 ? (e.target?.result as string).split(",")[1] : undefined,
                }
                resolveFile(result)
              }
              img.src = e.target?.result as string
            }
            reader.readAsDataURL(file)
          })
        }

        if (options?.multiple) {
          Promise.all(Array.from(files).map(processFile)).then(resolve).catch(reject)
        } else {
          processFile(files[0]).then(resolve).catch(reject)
        }
      }

      input.oncancel = () => {
        reject(new Error("User cancelled image selection"))
      }

      input.click()
    })
  },

  openCamera: (options?: Options): Promise<ImageOrVideo> => {
    return new Promise((resolve, reject) => {
      Alert.alert("Camera não disponível", 'A câmera não está disponível na versão web. Use "Selecionar da galeria".', [
        {
          text: "OK",
          onPress: () => reject(new Error("Camera not available on web")),
        },
      ])
    })
  },

  openCropper: (options: Options & { path: string }): Promise<ImageOrVideo> => {
    return new Promise((resolve) => {
      // Simula o crop retornando a mesma imagem
      resolve({
        path: options.path,
        width: options.width || 512,
        height: options.height || 512,
        mime: "image/jpeg",
        size: 50000,
        filename: "cropped-image.jpg",
      })
    })
  },

  clean: (): Promise<void> => {
    return Promise.resolve()
  },

  cleanSingle: (path: string): Promise<void> => {
    return Promise.resolve()
  },
}

export default ImageCropPicker

// Named exports
export const openPicker = ImageCropPicker.openPicker
export const openCamera = ImageCropPicker.openCamera
export const openCropper = ImageCropPicker.openCropper
export const clean = ImageCropPicker.clean
export const cleanSingle = ImageCropPicker.cleanSingle
